import React from "react";

import { Handle, Position } from "@xyflow/react";

import { Chip, Tooltip, Box, Typography } from "@mui/material";

import { NODE_COLOR_MAP } from "@/diagramme/config";

/**
 * BaseNode component
 * @description Component to render a node in the React Flow diagram.
 * @param {NodeProps<ReactflowNodeData>} props - Component props
 * @returns {JSX.Element} The rendered component
 */
export function BaseNode({ data }: any) {
  const isSuppressed = data.is_suppressed === true;
  const nodeType = data.type;
  const isOverview = data.isOverview === true;

  // Nouveau code couleur pour la vue par défaut (style légende) - sans bordures
  const getDefaultViewStyle = () => {
    if (nodeType === "commune") {
      return { bg: "#9e9e9e", text: "#fff", border: "none" };
    }
    if (nodeType === "cadastre") {
      return isSuppressed
        ? { bg: "#e8f5e9", text: "#2e7d32", border: "none" }
        : { bg: "#4caf50", text: "#fff", border: "none" };
    }
    if (nodeType === "volume") {
      return isSuppressed
        ? { bg: "#ffe0b2", text: "#e65100", border: "none" }
        : { bg: "#f57c00", text: "#fff", border: "none" };
    }
    if (nodeType === "lot") {
      return isSuppressed
        ? { bg: "#bbdefb", text: "#0d47a1", border: "none" }
        : { bg: "#1976d2", text: "#fff", border: "none" };
    }
    return { bg: "#9e9e9e", text: "#fff", border: "none" };
  };

  // Couleur pour la vue d'ensemble (style simple outline)
  const mainColor =
    nodeType === "commune"
      ? NODE_COLOR_MAP.commune.main
      : nodeType === "cadastre"
        ? NODE_COLOR_MAP.cadastre.main
        : nodeType === "lot"
          ? NODE_COLOR_MAP.lot.main
          : NODE_COLOR_MAP.volume.main;

  const defaultStyle = getDefaultViewStyle();

  // Rendu pour le mode Overview (parcelles supprimées - feuilles remplies, reste outline)
  if (isOverview) {
    const isLeafNode = data.isLeafNode === true;
    
    // Style pour overview: feuilles remplies, autres en outline
    const overviewChipStyle = isLeafNode
      ? {
          fontSize: "11px",
          fontWeight: 500,
          backgroundColor: mainColor,
          color: "#fff",
          border: "none",
          maxWidth: "none",
          '& .MuiChip-label': { 
            color: "#fff",
            overflow: "visible",
            whiteSpace: "nowrap",
          },
        }
      : {
          fontSize: "11px",
          fontWeight: 500,
          color: mainColor,
          border: `1px solid ${mainColor}`,
          backgroundColor: "transparent",
          maxWidth: "none",
          '& .MuiChip-label': { 
            overflow: "visible",
            whiteSpace: "nowrap",
          },
        };
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <Handle type="target" position={Position.Top} />
        <Tooltip
          title={
            <Box sx={{ p: 0.5, minWidth: 200 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: "block", fontSize: "12px", mb: 0.5 }}>
                {data.value}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", color: "grey.300" }}>
                <strong>Type:</strong> {data.type}
              </Typography>
              {data.is_suppressed !== undefined && (
                <Typography variant="caption" sx={{ display: "block", color: "grey.300" }}>
                  <strong>Statut:</strong> {data.is_suppressed ? "Supprimé" : "Existant"}
                </Typography>
              )}
            </Box>
          }
          arrow
          placement="top"
        >
          <Chip
            size="small"
            label={data.value}
            sx={overviewChipStyle}
          />
        </Tooltip>
        <Handle type="source" position={Position.Bottom} />
      </Box>
    );
  }

  // Rendu par défaut (vue par date) - nouveau code couleur (style légende)
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      <Handle type="target" position={Position.Top} />
      <Tooltip
        title={
          <Box sx={{ p: 0.5, minWidth: 200 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block", fontSize: "12px", mb: 0.5 }}>
              {data.value}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", color: "grey.300" }}>
              <strong>Type:</strong> {data.type}
            </Typography>
            {data.is_suppressed !== undefined && (
              <Typography variant="caption" sx={{ display: "block", color: "grey.300" }}>
                <strong>Statut:</strong> {data.is_suppressed ? "Supprimé" : "Existant"}
              </Typography>
            )}
          </Box>
        }
        arrow
        placement="top"
      >
        <Chip
          size="small"
          label={data.value}
          sx={{
            fontSize: "10px",
            backgroundColor: defaultStyle.bg,
            color: defaultStyle.text,
            border: defaultStyle.border,
            '& .MuiChip-label': {
              color: defaultStyle.text,
            },
          }}
        />
      </Tooltip>
      <Handle type="source" position={Position.Bottom} />
    </Box>
  );
}

BaseNode.displayName = "BaseNode";
